import { COLORS } from '@repo/design-system/index'
import { QIcon_chevron_bold_left, QIcon_chevron_bold_right } from '@repo/icons/index'
import { format, formatISO } from 'date-fns'
import DayPicker, { LocaleUtils } from 'react-day-picker'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { timezonedNow } from '../../../../modules/utils'

export const ScheduleCalendar = ({ selectedDay, setSelectedDay, classtimes, isTutorProfile = false }) => {
  const { isLocaleKO, locale } = useTranslation()
  const { currentUser } = useCurrentUser()
  const { timezone } = currentUser || {}

  const handleDayEnter = (date: number | Date) => {
    const isPast = formatISO(new Date(date), { representation: 'date' }) <= formatISO(timezonedNow(timezone))
    if (isPast) {
      return
    }
  }

  const stringToDate = (date: string) => {
    if (!date) return new Date()
    const [year, month, day] = date.split('-').map(Number)
    return new Date(year, month - 1, day)
  }
  const enabledDates = isTutorProfile
    ? classtimes
        ?.filter((classtime) => classtime?.timeslots?.filter((time) => time.is_opened)?.length > 0)
        .map((classtime) => stringToDate(classtime.date))
    : classtimes
        ?.filter((classtime) => classtime?.classtimes?.filter((time) => time.is_opened)?.length > 0)
        .map((classtime) => stringToDate(classtime.date))

  const isDayDisabled = (day) => {
    return !enabledDates?.some(
      (enabledDate) =>
        day.getDate() === enabledDate.getDate() &&
        day.getMonth() === enabledDate.getMonth() &&
        day.getFullYear() === enabledDate.getFullYear()
    )
  }
  const getSelectedDaysToDate = () =>
    isTutorProfile
      ? classtimes
          ?.filter((classtime) => classtime?.timeslots?.filter((time) => time.lesson)?.length > 0)
          .map((classtime) => stringToDate(classtime.date))
      : classtimes
          ?.filter((classtime) => classtime?.classtimes?.filter((time) => time.lesson)?.length > 0)
          .map((classtime) => stringToDate(classtime.date))
  const getTodayLessonsDate = () =>
    isTutorProfile
      ? classtimes
          ?.filter(
            (classtime) => classtime?.timeslots?.filter((time) => time.is_available && time.is_today)?.length > 0
          )
          .map((classtime) => stringToDate(classtime.date))
      : classtimes
          ?.filter(
            (classtime) => classtime?.classtimes?.filter((time) => time.is_available && time.is_today)?.length > 0
          )
          .map((classtime) => stringToDate(classtime.date))

  const onDayClick = (day, modifiers) => {
    const { disabled } = modifiers
    if (!disabled) {
      setSelectedDay(day)
    }
  }

  const getLocale = (locale) => {
    switch (locale) {
      case 'ko':
        return 'ko'
      case 'en':
        return 'en'
      case 'zh_chs':
        return 'zh-cn'
      case 'zh_cht':
        return 'zh-tw'
      case 'ja':
        return 'ja'
      case 'vi':
        return 'vi'
    }
  }

  const getWeekdaysShort = (locale) => {
    switch (locale) {
      case 'ko':
        return ['일', '월', '화', '수', '목', '금', '토']
      case 'en':
        return ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
      case 'zh_chs':
        return ['日', '一', '二', '三', '四', '五', '六']
      case 'zh_cht':
        return ['日', '一', '二', '三', '四', '五', '六']
      case 'ja':
        return ['日', '月', '火', '水', '木', '金', '土']
      case 'vi':
        return ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7']
    }
  }

  const CustomNavbar = ({ nextMonth, previousMonth, onPreviousClick, onNextClick }) => {
    const isMonthDisabled = (month) => {
      return !enabledDates?.some(
        (enabledDate) =>
          month.getMonth() === enabledDate.getMonth() && month.getFullYear() === enabledDate.getFullYear()
      )
    }
    return (
      <div className="absolute right-0 flex gap-x-8 items-center p-[4px]">
        <button
          role="button"
          aria-label="Previous Month"
          onClick={() => !isMonthDisabled(previousMonth) && onPreviousClick()}
          disabled={isMonthDisabled(previousMonth)}
        >
          <QIcon_chevron_bold_left
            width={20}
            height={20}
            color={isMonthDisabled(previousMonth) ? COLORS.GRAY300 : COLORS.BLACK}
          />
        </button>

        <button
          onClick={() => !isMonthDisabled(nextMonth) && onNextClick()}
          disabled={isMonthDisabled(nextMonth)}
          role="button"
          aria-label="Next Month"
        >
          <QIcon_chevron_bold_right
            width={20}
            height={20}
            color={isMonthDisabled(nextMonth) ? COLORS.GRAY300 : COLORS.BLACK}
          />
        </button>
      </div>
    )
  }
  const CustomCaption = ({ date, localeUtils }) => {
    return (
      <div className="DayPicker-Caption Title2-18Md sm:Title1-20Bd">
        {isLocaleKO ? (
          <>
            {date.getFullYear()}년 {date.getMonth() + 1}월
          </>
        ) : (
          <>{format(date, 'MMM, yyyy')}</>
        )}
      </div>
    )
  }

  return (
    <div className="schedule-bottom-sheet">
      <DayPicker
        showOutsideDays={true}
        captionElement={(props) => <CustomCaption {...props} />}
        firstDayOfWeek={0}
        navbarElement={(props) => <CustomNavbar {...props} />}
        localeUtils={LocaleUtils}
        enableOutsideDaysClick={false}
        onDayMouseEnter={handleDayEnter}
        selectedDays={[selectedDay]}
        weekdaysShort={getWeekdaysShort(locale)}
        locale={getLocale(locale)}
        onDayClick={onDayClick}
        disabledDays={isDayDisabled}
        initialMonth={selectedDay}
        modifiers={{
          lessons: getSelectedDaysToDate(),
          today: getTodayLessonsDate(),
        }}
      />
    </div>
  )
}
